import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";
import api, { API } from "../api";
import { toast } from "../hooks/use-toast";

const PlayerContext = createContext(null);

/**
 * What was playing when the tab closed.
 *
 * The server already stores progress per book, but only for owned books and
 * only once the position is worth keeping. This is the other half: which book
 * and chapter the bar was showing, so a refresh puts the listener back where
 * they were instead of on an empty page. Kept in localStorage because it has to
 * survive a reload and it is per-device, not per-account.
 */
const NOW_PLAYING_KEY = "trellure_now_playing";

function readNowPlaying() {
  try {
    const raw = localStorage.getItem(NOW_PLAYING_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v.bookId === "string" ? v : null;
  } catch {
    return null;
  }
}

function writeNowPlaying(v) {
  try {
    if (v) localStorage.setItem(NOW_PLAYING_KEY, JSON.stringify(v));
    else localStorage.removeItem(NOW_PLAYING_KEY);
  } catch {
    /* private mode, quota, or storage blocked: resume is a convenience */
  }
}

/** Speed and volume are per-listener habits, not per-session choices. */
const PREFS_KEY = "trellure_player_prefs";

function readPrefs() {
  try {
    const v = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
    return {
      rate: typeof v.rate === "number" && v.rate > 0 ? v.rate : 1,
      volume: typeof v.volume === "number" && v.volume >= 0 && v.volume <= 1 ? v.volume : 0.9,
    };
  } catch {
    return { rate: 1, volume: 0.9 };
  }
}

export function PlayerProvider({ children }) {
  const audioRef = useRef(typeof Audio !== "undefined" ? new Audio() : null);
  const currentRef = useRef(null);
  const playTrackRef = useRef(null);
  const lastSaveRef = useRef(0);
  const resumeRef = useRef(0); // position to seek once metadata loads
  // The signed URL currently loaded, and when it stops being usable.
  const sessionRef = useRef(null);
  // Guards against two recovery attempts racing on the same stall.
  // Holds the in-flight re-sign, so concurrent callers share one request.
  const swappingRef = useRef(null);
  // Whether the listener means to be playing, which is not the same as
  // whether the element happens to be paused right now.
  const wasPlayingRef = useRef(false);

  const [current, setCurrent] = useState(null); // { book, chapter, isSample }
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => readPrefs().volume);
  const [rate, setRateState] = useState(() => readPrefs().rate);
  // True while the bar is showing a restored track whose audio has not been
  // fetched yet: the URL is signed on demand, so nothing loads until play.
  const [needsResume, setNeedsResume] = useState(false);
  // Between pressing play and the first sound there are two round trips:
  // signing the URL, then Bunny. Without this the bar sat at 0:00 with a
  // play icon, looking like the press had not registered.
  const [buffering, setBuffering] = useState(false);
  const [sleepMinutes, setSleepMinutes] = useState(0); // 0 = off
  const [sleepRemaining, setSleepRemaining] = useState(0); // seconds
  const [error, setError] = useState(null);

  /** Mirrors the current position into localStorage. Cheap, so it runs on every tick. */
  const stashLocal = useCallback(() => {
    const audio = audioRef.current;
    const cur = currentRef.current;
    if (!audio || !cur) return;
    writeNowPlaying({
      bookId: cur.book.id,
      chapterId: cur.chapter?.id ?? null,
      isSample: Boolean(cur.isSample),
      position: audio.currentTime || 0,
      duration: audio.duration || 0,
      updatedAt: Date.now(),
    });
  }, []);

  const saveProgress = useCallback((force = false) => {
    stashLocal();
    const audio = audioRef.current;
    const cur = currentRef.current;
    if (!audio || !cur || cur.isSample || !cur.chapter) return;
    const now = Date.now();
    if (!force && now - lastSaveRef.current < 8000) return;
    lastSaveRef.current = now;
    api
      .post("/me/progress", {
        bookId: cur.book.id,
        chapterId: cur.chapter.id,
        position: audio.currentTime || 0,
        duration: audio.duration || 0,
      })
      .catch(() => {});
  }, [stashLocal]);

  /**
   * Bunny signatures expire — SIGNED_URL_SECONDS, ten minutes originally — and
   * the URL loaded into the audio element goes with them. While the browser
   * plays out of its buffer nothing breaks; the moment it asks for more bytes
   * with a stale token, Bunny answers 403 and playback simply stops.
   *
   * The endpoint has always returned `refreshAfterSeconds` for this. Rather
   * than swapping the source on a timer, which would stutter every nine minutes
   * whether or not it was needed, the fresh URL is fetched at the points where
   * a reload is invisible anyway: resuming from pause, seeking, and recovering
   * from a stall. Continuous listening is never interrupted.
   */
  const rememberSession = useCallback((data) => {
    const refreshAfter = Number(data?.refreshAfterSeconds);
    sessionRef.current = {
      // Without the hint, assume the shortest window the API has ever used and
      // treat the URL as stale early — refreshing too often costs one request,
      // refreshing too late costs the listener their place.
      staleAt: Date.now() + (Number.isFinite(refreshAfter) && refreshAfter > 0 ? refreshAfter : 540) * 1000,
    };
  }, []);

  const sessionIsStale = () =>
    !sessionRef.current || Date.now() >= sessionRef.current.staleAt;

  /**
   * Re-signs the current track and reloads it in place, keeping the position.
   *
   * `autoplay` is passed rather than read off the element: on the resume path
   * the audio is paused precisely because the listener paused it, so asking
   * `!audio.paused` there would re-sign and then sit there in silence.
   */
  const swapToFreshUrl = useCallback(({ autoplay = null, at = null } = {}) => {
    const audio = audioRef.current;
    const cur = currentRef.current;
    if (!audio || !cur) return Promise.resolve(false);

    // The position to land on. Callers that already know it — a seek, a skip —
    // pass it; otherwise hold whatever is playing now.
    resumeRef.current = at !== null ? at : (audio.currentTime || 0);

    // Tapping +15s a dozen times fires a dozen of these. Sharing the in-flight
    // promise means one re-sign for all of them: returning false to the extras
    // made every click after the first look like a failure and painted the
    // player as stalled while it was busy recovering.
    if (swappingRef.current) return swappingRef.current;

    const wasPlaying = autoplay === null ? !audio.paused : autoplay;
    const run = (async () => {
      try {
        const res = cur.isSample
          ? await api.post("/playback/sample", { bookId: cur.book.id })
          : await api.post("/playback/sessions", {
              bookId: cur.book.id,
              chapterId: cur.chapter?.id ?? null,
            });
        if (!res.data?.url) return false;
        rememberSession(res.data);
        audio.src = res.data.url;
        audio.load(); // loadedmetadata seeks to resumeRef, set above
        if (wasPlaying) {
          audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        }
        return true;
      } catch (e) {
        return false;
      } finally {
        swappingRef.current = null;
      }
    })();
    swappingRef.current = run;
    return run;
  }, [rememberSession]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      setProgress(audio.currentTime);
      saveProgress(false);
    };
    const onMeta = () => {
      setDuration(audio.duration || 0);
      if (resumeRef.current > 0 && resumeRef.current < (audio.duration || Infinity)) {
        audio.currentTime = resumeRef.current;
        setProgress(resumeRef.current);
      }
      resumeRef.current = 0;
      audio.playbackRate = rate;
    };
    const onEnd = () => {
      saveProgress(true);
      const cur = currentRef.current;
      if (cur && !cur.isSample && cur.book?.chapters?.length && playTrackRef.current) {
        const idx = cur.book.chapters.findIndex((c) => c.id === cur.chapter?.id);
        const next = cur.book.chapters[idx + 1];
        if (next) {
          playTrackRef.current(cur.book, next, false);
          return;
        }
      }
      setIsPlaying(false);
    };
    /**
     * The element failing is almost always the signature having expired
     * mid-chapter, so the first response is to re-sign and carry on rather than
     * to tell the listener something broke. Only if that fails does the error
     * surface, because at that point it is the network or the file itself.
     */
    const onError = async () => {
      if (!currentRef.current) return;
      setBuffering(true);
      // A stall while playing should come back playing; one while paused should
      // not start on its own.
      const recovered = await swapToFreshUrl({ autoplay: wasPlayingRef.current });
      if (!recovered) {
        setBuffering(false);
        setIsPlaying(false);
        setError("stalled");
      }
    };
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => { setBuffering(false); setError(null); setIsPlaying(true); };
    const onCanPlay = () => setBuffering(false);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onError);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [saveProgress, rate, swapToFreshUrl]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Speed and volume outlive the session: someone who listens at 1.5x expects
  // to still be at 1.5x tomorrow.
  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ rate, volume }));
    } catch { /* storage blocked */ }
  }, [rate, volume]);

  const setRate = useCallback((r) => {
    setRateState(r);
    if (audioRef.current) audioRef.current.playbackRate = r;
  }, []);

  /**
   * Flush on the way out.
   *
   * `timeupdate` only fires while audio is playing and the server save is
   * throttled to 8 s, so closing the tab mid-chapter would otherwise lose the
   * last few seconds. `pagehide` is the event that actually fires on mobile,
   * where the tab is frozen rather than unloaded; `visibilitychange` covers
   * switching apps.
   */
  useEffect(() => {
    const flush = () => {
      stashLocal();
      const cur = currentRef.current;
      const audio = audioRef.current;
      if (!cur || cur.isSample || !cur.chapter || !audio) return;
      const body = JSON.stringify({
        bookId: cur.book.id,
        chapterId: cur.chapter.id,
        position: audio.currentTime || 0,
        duration: audio.duration || 0,
      });
      // A normal XHR is cancelled when the page goes away. `keepalive` hands the
      // request to the browser to finish on its own — and unlike sendBeacon it
      // still carries the Authorization header, which this endpoint requires.
      const token = localStorage.getItem("trellure_token");
      if (!token) return;
      try {
        fetch(`${API}/me/progress`, {
          method: "POST",
          keepalive: true,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body,
        }).catch(() => {});
      } catch {
        saveProgress(true);
      }
    };
    const onHidden = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [stashLocal, saveProgress]);

  /**
   * Put the bar back after a refresh.
   *
   * Only the metadata is restored, never the audio: the stream URL is signed on
   * demand and expires, so fetching one for a tab that may never be played
   * would burn a signature for nothing. The listener sees where they were and
   * presses play, which is also what browsers require — audio cannot start
   * without a gesture, so auto-resuming would fail silently anyway.
   */
  useEffect(() => {
    const saved = readNowPlaying();
    if (!saved) return;
    let cancelled = false;
    api
      .get(`/books/${saved.bookId}`)
      .then((res) => {
        const book = res.data;
        if (cancelled || !book) return;
        const chapter = saved.chapterId
          ? (book.chapters ?? []).find((c) => c.id === saved.chapterId) ?? null
          : null;
        // A chapter that no longer exists means the admin reshuffled the book;
        // drop the stale entry rather than restoring a broken bar.
        if (saved.chapterId && !chapter && !saved.isSample) {
          writeNowPlaying(null);
          return;
        }
        const cur = { book, chapter, isSample: Boolean(saved.isSample) };
        setCurrent(cur);
        currentRef.current = cur;
        setProgress(saved.position || 0);
        setDuration(saved.duration || 0);
        resumeRef.current = saved.position || 0;
        setNeedsResume(true);
      })
      .catch(() => writeNowPlaying(null));
    return () => { cancelled = true; };
  }, []);

  // sleep timer countdown
  useEffect(() => {
    if (!sleepMinutes) {
      setSleepRemaining(0);
      return;
    }
    setSleepRemaining(sleepMinutes * 60);
    const iv = setInterval(() => {
      setSleepRemaining((s) => {
        if (s <= 1) {
          clearInterval(iv);
          if (audioRef.current) audioRef.current.pause();
          setIsPlaying(false);
          setSleepMinutes(0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [sleepMinutes]);

  const playTrack = useCallback(async (book, chapter, isSample = false, resumeAt = 0) => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);
    setNeedsResume(false);
    setBuffering(true);
    saveProgress(true);
    setCurrent({ book, chapter, isSample });
    currentRef.current = { book, chapter, isSample };
    try {
      let url;
      let resolvedChapter = chapter;
      if (isSample) {
        const res = await api.post("/playback/sample", { bookId: book.id });
        url = res.data.url;
        rememberSession(res.data);
        resumeRef.current = resumeAt > 0 ? resumeAt : 0;
      } else {
        const res = await api.post("/playback/sessions", {
          bookId: book.id,
          chapterId: chapter?.id || null,
        });
        url = res.data.url;
        rememberSession(res.data);
        if (!resolvedChapter && book.chapters) {
          resolvedChapter = book.chapters.find((c) => c.id === res.data.chapterId);
        }
        const cur = { book, chapter: resolvedChapter, isSample };
        setCurrent(cur);
        currentRef.current = cur;
        // An explicit position wins: it comes from the bar the listener is
        // looking at, which is fresher than the throttled server copy.
        if (resumeAt > 0) {
          resumeRef.current = resumeAt;
        } else {
          try {
            const pr = await api.get(`/me/progress/${book.id}`);
            if (pr.data && pr.data.chapterId === resolvedChapter?.id && pr.data.position > 5) {
              resumeRef.current = pr.data.position;
            }
          } catch (e) {}
        }
      }
      if (!url) {
        setError("no_url");
        setBuffering(false);
        setIsPlaying(false);
        return;
      }
      audio.src = url;
      audio.playbackRate = rate;
      audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } catch (e) {
      const locked = e?.response?.status === 403;
      setError(locked ? "locked" : "error");
      setBuffering(false);
      setIsPlaying(false);
      setCurrent(null);
      currentRef.current = null;
      toast({
        title: locked ? "Locked audiobook" : "Playback error",
        description: locked ? "Add it to your library to listen to the full audiobook." : "Please try again.",
        variant: "destructive",
      });
    }
  }, [rate, saveProgress, rememberSession]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!current) return;
    // Restored after a refresh: nothing is loaded yet, so this first press is
    // what fetches the signed URL and seeks to where the listener left off.
    if (needsResume) {
      playTrackRef.current?.(current.book, current.chapter, current.isSample, progress);
      return;
    }
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      saveProgress(true);
      return;
    }
    // Coming back from a pause long enough to outlive the signature: re-sign
    // before pressing play, or Bunny refuses the first request for new bytes.
    if (sessionIsStale()) {
      setBuffering(true);
      swapToFreshUrl({ autoplay: true }).then((ok) => {
        if (!ok) {
          setBuffering(false);
          setError("stalled");
        }
      });
      return;
    }
    audio.play().then(() => setIsPlaying(true)).catch(() => {});
  }, [isPlaying, current, needsResume, progress, saveProgress, swapToFreshUrl]);

  const seek = useCallback((time) => {
    // Before the audio is loaded the bar is still scrubable: move the marker and
    // remember it, so playing starts from where it was dragged to.
    if (needsResume) {
      setProgress(time);
      resumeRef.current = time;
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    // Dragging the bar is exactly when the browser has to fetch bytes it has
    // not buffered, so a stale signature fails here first. Re-sign, and let
    // loadedmetadata land on the requested position.
    if (sessionIsStale()) {
      setProgress(time);
      setBuffering(true);
      swapToFreshUrl({ at: time, autoplay: wasPlayingRef.current }).then((ok) => {
        if (!ok) { setBuffering(false); setError("stalled"); }
      });
      return;
    }
    audio.currentTime = time;
    setProgress(time);
  }, [needsResume, swapToFreshUrl]);

  const skip = useCallback((delta) => {
    if (needsResume) {
      setProgress((p) => Math.max(0, Math.min(duration || 0, p + delta)));
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    const target = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + delta));
    // Jumping forward asks for bytes that are very likely outside the buffer,
    // which is the same expired-signature trap as dragging the bar.
    if (sessionIsStale()) {
      setProgress(target);
      setBuffering(true);
      swapToFreshUrl({ at: target, autoplay: wasPlayingRef.current }).then((ok) => {
        if (!ok) { setBuffering(false); setError("stalled"); }
      });
      return;
    }
    audio.currentTime = target;
    setProgress(target);
  }, [needsResume, duration, swapToFreshUrl]);

  const playNext = useCallback(() => {
    const cur = currentRef.current;
    if (!cur || cur.isSample || !cur.book?.chapters?.length) return;
    const idx = cur.book.chapters.findIndex((c) => c.id === cur.chapter?.id);
    const next = cur.book.chapters[idx + 1];
    if (next) playTrack(cur.book, next, false);
  }, [playTrack]);

  const playPrev = useCallback(() => {
    const cur = currentRef.current;
    if (!cur || cur.isSample || !cur.book?.chapters?.length) return;
    const idx = cur.book.chapters.findIndex((c) => c.id === cur.chapter?.id);
    const prev = cur.book.chapters[idx - 1];
    if (prev) playTrack(cur.book, prev, false);
  }, [playTrack]);

  // keep a ref so the audio "ended" handler can auto-advance
  useEffect(() => {
    playTrackRef.current = playTrack;
  }, [playTrack]);

  // Media Session API: lock-screen / car / headset controls
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    if (!current) {
      navigator.mediaSession.metadata = null;
      return;
    }
    const { book, chapter, isSample } = current;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: chapter?.title_en || (isSample ? "Sample" : book.title_en),
        artist: book.narrator || book.author,
        album: book.title_en,
        artwork: [{ src: book.cover, sizes: "512x512", type: "image/jpeg" }],
      });
      navigator.mediaSession.setActionHandler("play", () => togglePlay());
      navigator.mediaSession.setActionHandler("pause", () => togglePlay());
      navigator.mediaSession.setActionHandler("seekbackward", () => skip(-15));
      navigator.mediaSession.setActionHandler("seekforward", () => skip(15));
      navigator.mediaSession.setActionHandler("previoustrack", () => playPrev());
      navigator.mediaSession.setActionHandler("nexttrack", () => playNext());
    } catch (e) {}
  }, [current, togglePlay, skip, playNext, playPrev]);

  useEffect(() => {
    wasPlayingRef.current = isPlaying;
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    }
  }, [isPlaying]);

  const closePlayer = useCallback(() => {
    const audio = audioRef.current;
    saveProgress(true);
    if (audio) {
      audio.pause();
      audio.src = "";
    }
    setCurrent(null);
    currentRef.current = null;
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
    setNeedsResume(false);
    setBuffering(false);
    sessionRef.current = null;
    setSleepMinutes(0);
    // Closing the bar is a deliberate "I'm done": it should not come back on the
    // next refresh. The server-side progress stays, so the book still resumes
    // from the library.
    writeNowPlaying(null);
  }, [saveProgress]);

  return (
    <PlayerContext.Provider
      value={{
        current,
        isPlaying,
        progress,
        duration,
        volume,
        rate,
        sleepMinutes,
        sleepRemaining,
        error,
        needsResume,
        buffering,
        setVolume,
        setRate,
        setSleepMinutes,
        playTrack,
        togglePlay,
        seek,
        skip,
        playNext,
        playPrev,
        closePlayer,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
